<script setup>
import { reactive, watch, computed } from 'vue'
import FieldPicker from '../shared/FieldPicker.vue'

const props = defineProps({
    node: Object,
})

const emit = defineEmits(['update:config'])

const allFieldGroups = reactive({ groups: [] })

// Load field definitions to resolve types for operator filtering
async function loadFieldTypes() {
    try {
        const response = await fetch('/bo/communications/api/v1/workflows/field-definitions')
        const result = await response.json()
        allFieldGroups.groups = result.data || []
    } catch (e) {
        console.error('Failed to load field definitions', e)
    }
}
loadFieldTypes()

function getFieldType(key) {
    for (const group of allFieldGroups.groups) {
        const found = group.fields.find(f => f.key === key)
        if (found) return found.type
    }
    return 'text'
}

const operatorsByType = {
    date: [
        { value: 'is_null', label: 'Is Empty' },
        { value: 'is_not_null', label: 'Is Not Empty' },
        { value: 'becomes_not_null', label: 'Became Set Since Entry' },
        { value: 'becomes_null', label: 'Became Empty Since Entry' },
        { value: 'changed', label: 'Changed Since Entry' },
    ],
    number: [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'gt', label: 'Greater Than' },
        { value: 'gte', label: 'Greater Than or Equal' },
        { value: 'lt', label: 'Less Than' },
        { value: 'lte', label: 'Less Than or Equal' },
        { value: 'is_null', label: 'Is Empty' },
        { value: 'is_not_null', label: 'Is Not Empty' },
        { value: 'changed', label: 'Changed Since Entry' },
    ],
    text: [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Not Contains' },
        { value: 'is_null', label: 'Is Empty' },
        { value: 'is_not_null', label: 'Is Not Empty' },
        { value: 'changed', label: 'Changed Since Entry' },
    ],
}

const noValueOperators = ['is_null', 'is_not_null', 'becomes_not_null', 'becomes_null', 'changed']

function getOperatorsForRule(rule) {
    const type = getFieldType(rule.field)
    if (type === 'date') return operatorsByType.date
    if (type === 'number') return operatorsByType.number
    return operatorsByType.text
}

function makeRule() {
    return { field: '', operator: 'equals', value: '' }
}

const form = reactive({
    operator: props.node?.data?.config?.operator ?? 'AND',
    rules: (props.node?.data?.config?.rules ?? []).length > 0
        ? [...props.node.data.config.rules]
        : [makeRule()],
})

watch(() => props.node, (node) => {
    if (!node) return
    form.operator = node.data?.config?.operator ?? 'AND'
    form.rules = (node.data?.config?.rules ?? []).length > 0 ? [...node.data.config.rules] : [makeRule()]
})

function onFieldChange(rule) {
    // Reset operator to first valid option for the new field type
    const operators = getOperatorsForRule(rule)
    if (!operators.find(o => o.value === rule.operator)) {
        rule.operator = operators[0]?.value ?? 'equals'
    }
    emitUpdate()
}

function addRule() {
    form.rules.push(makeRule())
    emitUpdate()
}

function removeRule(index) {
    form.rules.splice(index, 1)
    emitUpdate()
}

function emitUpdate() {
    emit('update:config', { operator: form.operator, rules: [...form.rules] })
}
</script>

<template>
    <div class="config-panel__section">
        <label class="config-panel__label">Match</label>
        <div class="condition-builder__operator-row">
            <button
                class="condition-builder__operator-btn"
                :class="{ active: form.operator === 'AND' }"
                @click="form.operator = 'AND'; emitUpdate()"
                type="button"
            >ALL (AND)</button>
            <button
                class="condition-builder__operator-btn"
                :class="{ active: form.operator === 'OR' }"
                @click="form.operator = 'OR'; emitUpdate()"
                type="button"
            >ANY (OR)</button>
        </div>

        <div class="condition-builder__rules">
            <div v-for="(rule, index) in form.rules" :key="index" class="condition-builder__rule">
                <div class="condition-builder__field-wrap">
                    <FieldPicker
                        v-model="rule.field"
                        placeholder="Select field..."
                        @update:modelValue="onFieldChange(rule)"
                    />
                </div>
                <select v-model="rule.operator" class="config-panel__select config-panel__select--sm" @change="emitUpdate">
                    <option v-for="o in getOperatorsForRule(rule)" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
                <input
                    v-if="!noValueOperators.includes(rule.operator)"
                    v-model="rule.value"
                    class="config-panel__input config-panel__input--sm"
                    type="text"
                    placeholder="Value"
                    @input="emitUpdate"
                />
                <button class="condition-builder__remove-btn" @click="removeRule(index)" type="button" title="Remove rule">&times;</button>
            </div>
        </div>

        <button class="condition-builder__add-btn" @click="addRule" type="button">+ Add Rule</button>
    </div>
</template>

<style scoped>
.condition-builder__operator-row {
    display: flex;
    gap: 6px;
    margin-bottom: 10px;
}
.condition-builder__operator-btn {
    flex: 1;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #d1d5db;
    background: white;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.15s;
}
.condition-builder__operator-btn.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
}
.condition-builder__rules {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
}
.condition-builder__rule {
    display: flex;
    gap: 4px;
    align-items: flex-start;
    flex-wrap: wrap;
}
.condition-builder__field-wrap {
    flex: 1;
    min-width: 160px;
}
.condition-builder__remove-btn {
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 8px 2px;
}
.condition-builder__add-btn {
    background: none;
    border: 1px dashed #9ca3af;
    color: #6b7280;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    width: 100%;
    transition: border-color 0.15s, color 0.15s;
}
.condition-builder__add-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
}
</style>
